import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import * as styles from './FacilityOptionPlanPanel.module.scss'

import {
  usePlanApi,
  usePropertyApi,
  usePropertyProvider,
  usePublication,
  useRoomApi,
  useRouteParams,
  useSearchQueryParams,
} from '../../../hooks'
import { formatJstDate } from '../../../utils'
import { Button, Col, Container, Row, Text } from '../../basics'
import { CalendarModal, FacilitySearchModal } from '../../modals'
import { Panel } from '../Panel'

const translateNamespace = 'components.Panel'

type PanelItemProps = {
  label: React.ReactNode
  value: React.ReactNode
  /**
   * When provided, render a "変更" button that invokes this callback. Used on
   * the plan-selection screen (`editable`) to open the calendar / facility
   * search modals. Omitted for read-only rows (the fixed チケット row) and on
   * non-editable screens.
   */
  action?: () => void
}

const PanelItem: React.FC<PanelItemProps> = ({ label, value, action }) => {
  const { t } = useTranslation()

  return (
    <div className={styles.panelItem}>
      <Container>
        <Row>
          <Col pc={action ? 9 : 12} sp={action ? 9 : 12}>
            <Text size={12} pcAlign="left" spAlign="left">
              {label}
            </Text>
            <Text size={14} weight="bold" pcAlign="left" spAlign="left">
              {value}
            </Text>
          </Col>
          {action && (
            <Col pc={3} sp={3}>
              <div className={styles.button}>
                <Button
                  display="inlineBlock"
                  htmlType="button"
                  outline
                  size="small"
                  variant="secondary"
                  onClick={action}
                >
                  {t(`${translateNamespace}.change`)}
                </Button>
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  )
}

export type FacilityOptionPlanPanelProps = {
  /**
   * When `true` (the plan-selection screen `/:property/:room/plans`), the date /
   * guest-count rows get a "変更" button that opens the calendar / facility
   * search modals. Changing a condition stays on the same screen — it only
   * updates the URL query params (with `replace: true`), so the chosen ticket
   * (`:room` path segment) stays fixed and the plan list re-fetches reactively.
   * See docs/specs/2026-06-06-plan-screen-search-edit-design.md.
   *
   * Defaults to `false` (read-only) so the shared option-selection screen is
   * unaffected.
   */
  editable?: boolean
}

export const FacilityOptionPlanPanel: React.FC<
  FacilityOptionPlanPanelProps
> = ({ editable = false }) => {
  const { t, i18n } = useTranslation()
  const {
    property: propertySlug,
    room: roomSlug,
    plan: planSlug,
  } = useRouteParams()
  const { params, onUpdateSearchParams } = useSearchQueryParams()
  const { checkInDate, numberOfAdults, numberOfChildren, numberOfInfants } =
    params
  const { isActivity } = usePropertyProvider()

  const [calendarIsOpen, setCalendarIsOpen] = useState(false)
  const [roomIsOpen, setRoomIsOpen] = useState(false)

  const { data: property } = usePropertyApi(propertySlug ?? '')
  const { data: roomResponse } = useRoomApi({
    property: propertySlug,
    room: roomSlug,
  })
  const { data: planResponse } = usePlanApi({
    property: propertySlug,
    room: roomSlug,
    plan: planSlug,
  })

  const publication = usePublication(i18n.language, property?.publications)
  const room = usePublication(i18n.language, roomResponse?.publications)
  const plan = usePublication(i18n.language, planResponse?.publications)

  const items = [
    // チケット(ticket) is fixed by the `:room` path segment, so it has no
    // "変更" button even when editable — only the search conditions can change.
    <PanelItem
      label={t(`${translateNamespace}.ticket`)}
      value={room?.name ?? ''}
    />,
    <PanelItem
      label={t(`${translateNamespace}.dateOfUse`)}
      value={formatJstDate(
        checkInDate,
        i18n.language === 'ja' ? 'yyyy/MM/dd (E)' : 'MM/dd/yyyy'
      )}
      action={editable ? () => setCalendarIsOpen(true) : undefined}
    />,
    <PanelItem
      label={t(`${translateNamespace}.people`)}
      value={
        <Trans
          i18nKey={`${translateNamespace}.${
            isActivity ? 'activityGuests' : 'facilityGuests'
          }`}
          value={{
            numberOfAdults,
            numberOfChildren,
            numberOfInfants,
          }}
        >
          {{ numberOfAdults }} adults
          <br />
          {{ numberOfChildren }} children
          <br />
          {{ numberOfInfants }} infants
        </Trans>
      }
      action={editable ? () => setRoomIsOpen(true) : undefined}
    />,
  ]

  if (plan) {
    items.splice(
      1,
      0,
      <PanelItem label={t(`${translateNamespace}.plan`)} value={plan.name} />
    )
  }

  return (
    <React.Fragment>
      <Panel
        cover={{
          name: publication?.name ?? '',
          image: property?.images?.[0]?.url ?? '/img/hotel01.jpg',
          desc: plan
            ? t(`${translateNamespace}.action.option`)
            : t(`${translateNamespace}.action.plan`),
        }}
        label={t(`${translateNamespace}.label.ticket`)}
        items={items}
      />
      {editable && (
        <React.Fragment>
          <CalendarModal
            propertyName={publication?.name ?? ''}
            // Facilities (spa) are single-day use, but the backend requires
            // both checkIn and checkOut — keep them equal and run the calendar
            // in single mode (mirrors FacilitySearchPanel).
            checkInParam={checkInDate}
            checkOutParam={checkInDate}
            isOpen={calendarIsOpen}
            onClose={() => setCalendarIsOpen(false)}
            onSearch={(checkIn, checkOut) => {
              // Stay on the plan screen: only update the URL query params (with
              // replace so changing conditions doesn't pollute history). The
              // `:room` path segment is untouched, so the chosen ticket stays
              // fixed and PlanIndex re-fetches reactively.
              onUpdateSearchParams(
                { checkInDate: checkIn, checkOutDate: checkOut },
                true
              )
              setCalendarIsOpen(false)
            }}
            mode="single"
            key={checkInDate}
          />
          <FacilitySearchModal
            isOpen={roomIsOpen}
            onClose={() => setRoomIsOpen(false)}
            propertyName={publication?.shortName ?? ''}
            roomParam={params.roomCount}
            adultParam={numberOfAdults}
            childParam={numberOfChildren}
            infantParam={numberOfInfants}
            checkIn={checkInDate}
            onSearch={(rooms, adults, children, infants) => {
              onUpdateSearchParams(
                {
                  roomCount: rooms,
                  numberOfAdults: adults,
                  numberOfChildren: children,
                  numberOfInfants: infants,
                },
                true
              )
              setRoomIsOpen(false)
            }}
            key={`${params.roomCount}${numberOfAdults}${numberOfChildren}${numberOfInfants}`}
            isActivity={isActivity}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
